import { Barcode, Camera, CloudDownload, Image, ImageOff, Plus, RefreshCw, Search } from 'lucide-react'
import { useState } from 'react'

import { Button, ProductThumb } from '@krp/ui-kit'

import '../../../shared/ui/onec.css'
import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { InputField } from '../../../shared/ui/InputField'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import type { ProductsActions, ProductsVm } from '../model/types'
import { BarcodeScanner } from './scanner/BarcodeScanner'

/**
 * Товары одной базы 1С: поиск по наименованию/артикулу и по штрихкоду (поле,
 * сканер-клавиатура или камера). Экран получает vm и actions — ни store, ни
 * HTTP. База окна — в адресе: открытый список другой базы этим не задевается.
 * «Обновить» перечитывает найденное; «Обновить каталог из 1С» — команда службе 1С
 * обновить её зеркало каталога этой базы (`Catalog_TriggerSync`), итог — счётчиками.
 */
export function ProductsScreen({ vm, actions }: { vm: ProductsVm; actions: ProductsActions }) {
  const [scanning, setScanning] = useState(false)
  const searched = vm.submitted
  return (
    <section className="onec-screen" aria-labelledby="onec-products-title">
      <header className="onec-toolbar">
        <h2 id="onec-products-title">Товары 1С</h2>
        <Button variant="primary" icon={<Plus size={16} aria-hidden="true" />} onClick={actions.create}>
          Новый товар
        </Button>
        <Button
          variant="ghost"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          busy={vm.query.refreshing}
          onClick={actions.reload}
          disabled={searched === null}
        >
          Обновить
        </Button>
        <Button
          variant="ghost"
          icon={<CloudDownload size={16} aria-hidden="true" />}
          busy={vm.catalogRefresh?.status === 'sending'}
          disabled={vm.catalogRefresh?.status === 'sending' || vm.catalogRefresh?.status === 'unknown'}
          onClick={actions.refreshCatalog}
        >
          Обновить каталог из 1С
        </Button>
      </header>

      {vm.catalogRefresh !== null && (
        <CommandStatus
          command={vm.catalogRefresh}
          onResend={() => vm.catalogRefresh !== null && actions.resendOperation(vm.catalogRefresh.key)}
          onDismiss={() => vm.catalogRefresh !== null && actions.dismissOperation(vm.catalogRefresh.key)}
        />
      )}

      <div className="onec-search" role="search">
        <InputField
          type="search"
          label="Наименование, артикул или штрихкод"
          value={vm.text}
          onChange={actions.textChanged}
          onEnter={actions.searchSubmitted}
        />
        <div className="onec-search__buttons">
          <Button variant="primary" icon={<Search size={16} aria-hidden="true" />} onClick={actions.searchSubmitted}>
            Найти
          </Button>
          <Button
            icon={<Barcode size={16} aria-hidden="true" />}
            onClick={() => actions.barcodeSubmitted(vm.text)}
            disabled={vm.text.trim() === ''}
          >
            По штрихкоду
          </Button>
          {vm.cameraScanAvailable && (
            <Button icon={<Camera size={16} aria-hidden="true" />} onClick={() => setScanning(true)}>
              Сканировать
            </Button>
          )}
        </div>
      </div>

      {searched === null ? (
        <p>Введите наименование, артикул или отсканируйте штрихкод.</p>
      ) : (
        <p aria-live="polite">
          {searched.mode === 'barcode' ? `Штрихкод ${searched.value}` : `Поиск «${searched.value}»`}
          {vm.query.status === 'ready' ? ` — найдено ${vm.rows.length}` : ''}
        </p>
      )}

      <OneCQueryState
        query={vm.query}
        emptyText={searched?.mode === 'barcode' ? 'Товара с таким штрихкодом в этой базе нет' : 'Ничего не найдено'}
        onRetry={actions.reload}
      />

      {vm.query.status === 'ready' && (
        <ul className="onec-list" aria-label="Найденные товары">
          {vm.rows.map((row) => (
            <li key={row.refKey}>
              {row.photo != null && <ProductThumb src={row.photo} name={row.name} size={44} />}
              <Button variant="ghost" onClick={() => actions.open(row.refKey)}>
                {row.name}
              </Button>
              <span className="onec-list__meta">
                {row.sku !== null ? `арт. ${row.sku}` : 'без артикула'}
                {row.barcode !== null ? ` · ${row.barcode}` : ''}
              </span>
              {row.hasPhoto !== null && (
                <span className="onec-list__meta">
                  {row.hasPhoto ? <Image size={14} aria-hidden="true" /> : <ImageOff size={14} aria-hidden="true" />}
                  {row.hasPhoto ? ' фото есть' : ' без фото'}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {scanning && (
        <BarcodeScanner
          onClose={() => setScanning(false)}
          onCode={(code) => {
            setScanning(false)
            actions.barcodeSubmitted(code)
          }}
        />
      )}
    </section>
  )
}
